export const REDACTED = '[Redacted]'
const CIRCULAR = '[Circular]'
const MAX_DEPTH = 6
const MAX_NODES = 10000

const SENSITIVE_KEY_PATTERN = /password|passwd|passphrase|secret|token(?!s)|authorization|credential|privatekey|apikey|verifier|cookie|signature|bearer|jwt|twofactorcode|mfacode|recoverycode|backupcode/
const SENSITIVE_EXACT_KEYS = new Set([
  'otp',
  'otpcode',
  'totp',
  'pin',
  'cvv',
  'cvc',
  'ssn',
  'iban',
  'xcurrentlinkid',
])
const SENSITIVE_QUERY_EXACT_KEYS = new Set([
  'code',
  'state',
  'key',
  'sig',
  'hash',
  'auth',
  'nonce',
  'session',
  'sessionid',
  'idtoken',
  'accesstoken',
  'refreshtoken',
  'clientsecret',
])

const BEARER_OR_BASIC_PATTERN = /\b(bearer|basic)\s+[a-z0-9\-._~+/]+=*/gi
const JWT_PATTERN = /\beyJ[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}/g
const QUERY_PAIR_PATTERN = /([?&;#]|^|\s)([a-z0-9_.-]{1,128})=([^&\s"'<>]+)/gi
const JSON_PAIR_PATTERN = /("([^"\\]{1,128})"\s*:\s*)"(?:[^"\\]|\\.)*"/g
const SENSITIVE_PATH_PATTERNS = [
  /(\/two-factor\/totp-image\/)[^/?#\s"'<>]+/gi,
  /(\/workspace-invitation\/[^/?#\s"'<>-]+-)[^/?#\s"'<>]+/gi,
]

interface IRedactionState {
  nodes: number
  stack: Set<object>
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key)
  return SENSITIVE_EXACT_KEYS.has(normalized) || SENSITIVE_KEY_PATTERN.test(normalized)
}

export function isSensitiveQueryKey(key: string): boolean {
  const normalized = normalizeKey(key)
  return SENSITIVE_QUERY_EXACT_KEYS.has(normalized) || isSensitiveKey(key)
}

export function redactText(text: string): string {
  if(!text) {
    return text
  }
  return redactSensitivePathSegments(text)
    .replace(JWT_PATTERN, () => REDACTED)
    .replace(BEARER_OR_BASIC_PATTERN, (_match, scheme: string) => `${scheme} ${REDACTED}`)
    .replace(QUERY_PAIR_PATTERN, (match, prefix: string, key: string) => isSensitiveQueryKey(key) ? `${prefix}${key}=${REDACTED}` : match)
    .replace(JSON_PAIR_PATTERN, (match, prefix: string, key: string) => isSensitiveKey(key) ? `${prefix}"${REDACTED}"` : match)
}

export function redactUrl(url: unknown): unknown {
  if(typeof url !== 'string') {
    return url
  }
  const queryStart = url.indexOf('?')
  if(queryStart === -1) {
    return redactSensitivePathSegments(url)
  }
  const hashStart = url.indexOf('#', queryStart)
  const base = url.slice(0, queryStart)
  const query = hashStart === -1 ? url.slice(queryStart + 1) : url.slice(queryStart + 1, hashStart)
  const hash = hashStart === -1 ? '' : url.slice(hashStart)
  return `${redactSensitivePathSegments(base)}?${redactQueryString(query)}${hash}`
}

export function redactSensitivePathSegments(text: string): string {
  return SENSITIVE_PATH_PATTERNS.reduce(
    (result, pattern) => result.replace(pattern, (_match, prefix: string) => `${prefix}${REDACTED}`),
    text,
  )
}

export function redactQueryString(query: string): string {
  if(!query) {
    return query
  }
  return query
    .split('&')
    .map((pair) => {
      const separator = pair.indexOf('=')
      if(separator === -1) {
        return pair
      }
      const key = pair.slice(0, separator)
      return isSensitiveQueryKey(safeDecode(key)) ? `${key}=${REDACTED}` : pair
    })
    .join('&')
}

export function redactQuery(query: unknown): unknown {
  if(typeof query === 'string') {
    return redactQueryString(query)
  }
  if(!query || typeof query !== 'object') {
    return query
  }
  if(Array.isArray(query)) {
    return query.map((item) => redactQuery(item))
  }
  const result: Record<string, unknown> = {}
  for(const [key, value] of Object.entries(query as Record<string, unknown>)) {
    result[key] = isSensitiveQueryKey(key) ? REDACTED : redactSensitive(value)
  }
  return result
}

export function redactHeaders(headers: unknown): unknown {
  if(!headers || typeof headers !== 'object') {
    return headers
  }
  const result: Record<string, unknown> = {}
  for(const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    result[key] = isSensitiveKey(key) ? REDACTED : redactSensitive(value)
  }
  return result
}

export function redactSensitive(value: unknown, depth = 0, state: IRedactionState = createState()): unknown {
  if(typeof value === 'string') {
    return redactText(value)
  }
  if(value === null || typeof value !== 'object') {
    return value
  }
  if(depth > MAX_DEPTH) {
    return REDACTED
  }
  state.nodes += 1
  if(state.nodes > MAX_NODES) {
    return REDACTED
  }
  if(state.stack.has(value)) {
    return CIRCULAR
  }
  if(value instanceof Date) {
    return value
  }
  if(Buffer.isBuffer(value)) {
    return `[Buffer ${value.length} bytes]`
  }
  if(value instanceof Error) {
    return redactError(value, depth, state)
  }
  const serializable = value as {toJSON?: () => unknown}
  if(typeof serializable.toJSON === 'function') {
    state.stack.add(value)
    try {
      return redactSensitive(serializable.toJSON(), depth + 1, state)
    } finally {
      state.stack.delete(value)
    }
  }
  state.stack.add(value)
  try {
    if(Array.isArray(value)) {
      return value.map((item) => redactSensitive(item, depth + 1, state))
    }
    const result: Record<string, unknown> = {}
    for(const [key, item] of Object.entries(value as Record<string, unknown>)) {
      result[key] = isSensitiveKey(key) ? REDACTED : redactSensitive(item, depth + 1, state)
    }
    return result
  } finally {
    state.stack.delete(value)
  }
}

export function redactLogArguments(args: unknown[]): unknown[] {
  return args.map((arg) => redactSensitive(arg))
}

function redactError(error: Error, depth: number, state: IRedactionState): Error {
  const redacted = Object.create(Object.getPrototypeOf(error)) as Error
  defineHidden(redacted, 'message', redactText(error.message ?? ''))
  defineHidden(redacted, 'stack', redactText(error.stack ?? ''))
  if(Object.prototype.hasOwnProperty.call(error, 'name')) {
    defineHidden(redacted, 'name', error.name)
  }
  state.stack.add(error)
  try {
    for(const [key, item] of Object.entries(error)) {
      (redacted as unknown as Record<string, unknown>)[key] = isSensitiveKey(key) ? REDACTED : redactSensitive(item, depth + 1, state)
    }
    const cause = (error as {cause?: unknown}).cause
    if(cause !== undefined) {
      defineHidden(redacted, 'cause', redactSensitive(cause, depth + 1, state))
    }
  } finally {
    state.stack.delete(error)
  }
  return redacted
}

function defineHidden(target: object, key: string, value: unknown): void {
  Object.defineProperty(target, key, {
    value,
    enumerable: false,
    writable: true,
    configurable: true,
  })
}

function createState(): IRedactionState {
  return {
    nodes: 0,
    stack: new Set<object>(),
  }
}

export function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
